import { authClientFromEnv, createMockAuthClient } from "@allergy123/auth-client";
import { AuthProvider } from "@allergy123/auth-client/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router";
import { ClinicalProvider } from "./api";
import { router } from "./routes";
import "./styles.css";

const env = {
  AUTH_AUTHORITY: import.meta.env.VITE_AUTH_AUTHORITY as string | undefined,
  AUTH_CLIENT_ID: import.meta.env.VITE_AUTH_CLIENT_ID as string | undefined,
  AUTH_MOCK: import.meta.env.VITE_AUTH_MOCK as string | undefined,
  AUTH_MOCK_PERSONA: import.meta.env.VITE_AUTH_MOCK_PERSONA as string | undefined,
};
const apiBaseUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:8787/v1";

/**
 * The synthetic personas the local API knows (services/api/src/lib/fixtures.ts). Each carries `amr: mfa` so the
 * local API treats the session as a multi-factor sign-in; the deployed API takes identity from Cognito only.
 * `VITE_AUTH_MOCK_PERSONA=otherPracticeLearner` is the denial walk-through: a member of the other practice.
 */
const PERSONAS = {
  practiceAdmin: { sub: "mock-practice-admin", email: "manager@example.test", name: "Mock Manager", groups: [], amr: "mfa" },
  learner: { sub: "mock-user", email: "learner@example.test", name: "Mock Learner", groups: [], amr: "mfa" },
  otherPracticeLearner: { sub: "mock-other", email: "other@example.test", name: "Other Practice", groups: [], amr: "mfa" },
} as const;
const persona = PERSONAS[(env.AUTH_MOCK_PERSONA as keyof typeof PERSONAS | undefined) ?? "practiceAdmin"] ?? PERSONAS.practiceAdmin;

/**
 * Like apps/reps: the hosted build carries the pool (`VITE_AUTH_AUTHORITY`, `VITE_AUTH_CLIENT_ID`, from the Identity
 * stack's AuthAuthority and ClinicalClientId outputs) and signs in against Cognito with PKCE through the shared pages in
 * pages/auth.tsx. Without the pool, or with `VITE_AUTH_MOCK=1`, the mock client signs in as the chosen persona; the
 * persona variable never turns the mock on by itself, so a hosted build cannot be left on a mock by a stray setting.
 */
const useMock = !env.AUTH_AUTHORITY || !env.AUTH_CLIENT_ID || env.AUTH_MOCK === "1";
const clientPromise = useMock ? Promise.resolve(createMockAuthClient({ user: { ...persona, groups: [...persona.groups] } })) : authClientFromEnv(env, window.location.origin);

clientPromise.then((client) => {
  const token = async () => (await client.getSession())?.idToken ?? null;
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <AuthProvider client={client}>
        <ClinicalProvider baseUrl={apiBaseUrl} token={token}>
          <RouterProvider router={router} />
        </ClinicalProvider>
      </AuthProvider>
    </StrictMode>,
  );
});
